import React from 'react';
import { Reveal } from './motion';

/**
 * The width a written page uses.
 *
 * A page capped at 768px inside a 2560px window is mostly empty margin, but a
 * 1400px paragraph is unreadable — so widening the container is not on its own
 * an improvement. What fills the width without hurting the reading is moving
 * the section headings out beside the text: the prose keeps its measure and the
 * space to its left carries the structure of the document, which is how a
 * reference page is normally set.
 *
 * Below the wide breakpoint this collapses back to one column, heading above
 * body, which is the same thing these pages did before.
 */

export const Page: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mx-auto w-full max-w-3xl py-10 xl:max-w-6xl 2xl:max-w-page ${className}`}>{children}</div>
);

/**
 * One section of such a page: its heading in the rail, its content beside it.
 * `lead` opts out of the rail for an opening passage that should sit under its
 * own title at full measure.
 */
export const Section: React.FC<{
  title?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ title, aside, children, wide = false }) => (
  <Reveal>
  <section className="grid gap-x-12 gap-y-4 xl:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
    <div className="xl:pt-1">
      {title && (
        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white xl:sticky xl:top-24 xl:text-lg">
          {title}
        </h2>
      )}
      {aside && <div className="mt-2 hidden text-xs leading-relaxed text-slate-500 dark:text-slate-400 xl:block">{aside}</div>}
    </div>
    {/* Prose keeps its measure; a grid or a table may use the whole column. */}
    <div className={`space-y-4 ${wide ? '' : 'max-w-3xl'}`}>{children}</div>
  </section>
  </Reveal>
);
